// Stable, client-facing classification of a `Validate` failure (HTTP 400
// `InvitationValidationFailed`) or a terminal customer-lifecycle state (`Inactive`,
// derived from `customerLifecycleStatus`). Branch on this instead of the raw CCMT
// `errorCodes`; treat any unrecognized value as `Unknown` (forward-compatible).
export type InvitationValidationReason = 'Expired' | 'Invalid' | 'UsersLimitReached' | 'Inactive' | 'Unknown';

// Raw CCMT `CustomerLifecycleStatus` enum values surfaced on the `Validate` response
// (AdsAppUI PR 7008202). `Inactive`/`Deleted` are terminal: the invitation must not
// proceed to activation. Absent (`null`/`undefined`) on invitations predating the
// field — treat as "no status" and keep the normal flow (backward compatible).
export const CustomerLifecycleStatus = {
  Draft: 10,
  Active: 11,
  Inactive: 12,
  Deleted: 13,
} as const;

export type InvitationApiError = {
  error?: string;
  // Present on a `Validate` 400 `InvitationValidationFailed` response. See
  // InvitationValidationReason. Absent on other endpoints / error shapes.
  reason?: string;
  errorCodes?: string[];
  message?: string;
  status?: number;
  // Set on a non-contract 2xx whose body is not our JSON (auth/login HTML, CDN/stale page, or a
  // redirect that never reached our API), surfaced as `error: 'NonJsonResponse'`. These capture the
  // response `content-type` and final (post-redirect) URL so the failure is diagnosable/joinable to
  // a server log instead of being swallowed into an empty response.
  contentType?: string;
  url?: string;
  // Set on a 401 `ReauthRequired`: the server-side token cache lost this session's entry, so the
  // client must bounce the BROWSER through this challenge endpoint (a top-level navigation the
  // server cannot perform for an XHR) to mint fresh tokens and return to the invitation.
  authUrl?: string;
};

export type InvitationAccount = {
  name: string;
  number: string;
};

export type InvitationValidationResponse = {
  customerId?: number;
  customerName?: string | null;
  // The invited customer's human-readable account number (e.g. "B012345"). Added by CCMT/CCUI
  // (AdsAppUI PR 6938544); `null` for invitations validated before the server surfaced it.
  customerNumber?: string | null;
  isInternal?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  invitationFlags?: {
    aadSupported?: boolean;
    msaSupported?: boolean;
  };
  isFirstPartyInternal?: boolean;
  daysLeftToJoin?: number | null;
  // Raw CCMT `CustomerLifecycleStatus` byte (see `CustomerLifecycleStatus`); `null`/absent
  // for invitations predating AdsAppUI PR 7008202. `Inactive` (12) blocks activation.
  customerLifecycleStatus?: number | null;
  // Who sent the invitation (PII), the role the invitee will be granted, and the accounts/customer
  // the invitation grants access to — surfaced on the pre-auth landing page's summary line.
  senderName?: string | null;
  roleName?: string | null;
  // The accounts the invitation grants access to (account-level access), each with its display name
  // and account number. Capped at 3 by CCMT; `accountCount` is the true total so the UI can render
  // "… and N more". Empty ([]) for customer-level access.
  accounts?: InvitationAccount[] | null;
  accountCount?: number | null;
  // When true the invitation grants customer-level access (all accounts) — the summary uses
  // `customerName` instead of the account list.
  isCustomerLevelAccess?: boolean;
};

export type ActivationType = 'RegularActivation' | 'MultiuserActivation' | 'ActivationDenied' | 'ActivateWithAlternate' | 'UpgradeAndActivate' | 'Unknown';

export type ActivationOptionsResponse = {
  type: ActivationType;
  username?: string;
  identityType?: string;
  reason?: string;
  expectedUsername?: string;
  expectedIdentityType?: string;
  currentUsername?: string;
  currentIdentityType?: string;
  fromUsername?: string;
  toUsername?: string;
  isMissingMasterData?: boolean;
  existingUser?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    address?: {
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      stateOrProvince?: string;
      zipOrPostalCode?: string;
      country?: string;
    } | null;
  } | null;
};

export type InvitationActivateRequest = {
  activationCode: string;
  identityProvider: string;
  // Forwarded only so a 401 `ReauthRequired` response can stamp it on the re-auth callback URL and
  // land the user back on the same view. Servers predating that contract ignore the unknown field.
  intent?: string;
  userModel: {
    firstName?: string;
    lastName?: string;
    email?: string;
    workPhone?: string;
    languageId: number;
    address?: {
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      stateOrProvince?: string;
      country?: string;
      zipOrPostalCode?: string;
    };
    allowContactPreference?: boolean;
    contactPreferenceEmail?: boolean;
    contactPreferencePhone?: boolean;
    emailFormat?: string;
  };
};

export type InvitationActivateResponse = {
  success?: boolean;
  customerId?: number;
  username?: string;
  customerName?: string | null;
  customerNumber?: string | null;
  roleId?: number | null;
  redirectUrl?: string | null;
};

// Country / state-province option (a `Key`/`Value` pair). `key` is the 2-letter
// country code or the state/province code; `value` is the localized display name.
export type AddressSchemaOption = {
  key: string;
  value: string;
};

// Per-field address validation rule, derived from the `metadata` array returned
// by `GET /api/Invitation/LoadCountry/{countryCode}`. `field` is the wire field
// name used by `userModel.address` (e.g. `addressLine1`, `zipOrPostalCode`).
export type AddressFieldMetadata = {
  field: string;
  required: boolean;
  maxLength?: number;
  // Regex source the value must satisfy. `undefined` when the server returns no
  // rule or the generic passthrough (`[^\x00-\x1F]*`).
  match?: string;
  label?: string;
  // `false` hides the field entirely (e.g. `stateOrProvince` for some countries).
  visible: boolean;
  order?: number;
};

// Country list, per-country state/province list, and per-field validation
// metadata returned by `GET /api/Invitation/LoadCountry/{countryCode}`.
// `metadata` is keyed by the wire field name (`addressLine1`, `addressLine2`,
// `city`, `stateOrProvince`, `zipOrPostalCode`).
export type CountryStateSchema = {
  countries: AddressSchemaOption[];
  states: AddressSchemaOption[];
  metadata: Record<string, AddressFieldMetadata>;
};
